import React from "react";
import { Box, Container, Heading, Text, SimpleGrid, VStack, HStack, Icon, Badge, List, ListItem, ListIcon, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from "@chakra-ui/react";
import { FaUniversity, FaPercentage, FaShieldAlt, FaChartPie, FaCheckCircle, FaCreditCard, FaFileInvoiceDollar } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PropertyStrategyFinanceOptimization: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/strategy/finance-optimization"
        fallbackTitle="Finance Optimisation"
        fallbackDescription="Optimise your property finance structure for better outcomes"
      />
      <NavbarWithCallToAction />

      <Box bgGradient="linear(to-br, teal.500, green.600)" color="white" py={20}>
        <Container maxW="container.xl">
          <HStack spacing={12} justify="space-between">
            <VStack align="flex-start" spacing={6} maxW="2xl">
              <Badge bg="teal.300" px={4} py={2} borderRadius="full">Finance Strategy</Badge>
              <Heading size="3xl" fontWeight="black">Finance Optimisation</Heading>
              <Text fontSize="xl" lineHeight="1.8">
                Structure your property finance for maximum tax efficiency, cash flow, and growth potential
              </Text>
            </VStack>
            <Icon as={FaChartPie} boxSize={32} display={{ base: "none", lg: "block" }} opacity={0.3} />
          </HStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl">Finance Structure Strategies</Heading>
          <Text fontSize="lg" color="gray.600">Optimise your loan structure for better outcomes</Text>
        </VStack>

        <Accordion allowMultiple>
          <AccordionItem border="1px" borderColor="gray.200" borderRadius="lg" mb={4}>
            <AccordionButton py={6} _expanded={{ bg: "teal.50" }}>
              <HStack flex={1} textAlign="left">
                <Icon as={FaPercentage} boxSize={6} color="teal.600" />
                <Heading size="md">Interest-Only vs. Principal & Interest</Heading>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={6}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box>
                  <Heading size="sm" mb={3} color="teal.600">Interest-Only Benefits</Heading>
                  <List spacing={2}>
                    <ListItem display="flex"><ListIcon as={FaCheckCircle} color="teal.500" mt={1} />Higher cash flow</ListItem>
                    <ListItem display="flex"><ListIcon as={FaCheckCircle} color="teal.500" mt={1} />Tax deductible interest</ListItem>
                    <ListItem display="flex"><ListIcon as={FaCheckCircle} color="teal.500" mt={1} />Flexibility for investors</ListItem>
                  </List>
                </Box>
                <Box>
                  <Heading size="sm" mb={3} color="blue.600">P&I Benefits</Heading>
                  <List spacing={2}>
                    <ListItem display="flex"><ListIcon as={FaCheckCircle} color="blue.500" mt={1} />Build equity faster</ListItem>
                    <ListItem display="flex"><ListIcon as={FaCheckCircle} color="blue.500" mt={1} />Lower total interest</ListItem>
                    <ListItem display="flex"><ListIcon as={FaCheckCircle} color="blue.500" mt={1} />Better for long-term holds</ListItem>
                  </List>
                </Box>
              </SimpleGrid>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="1px" borderColor="gray.200" borderRadius="lg" mb={4}>
            <AccordionButton py={6} _expanded={{ bg: "green.50" }}>
              <HStack flex={1} textAlign="left">
                <Icon as={FaUniversity} boxSize={6} color="green.600" />
                <Heading size="md">Fixed vs. Variable Rate Strategy</Heading>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={6}>
              <Text color="gray.600" mb={4}>Split your loan for optimal risk management</Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box bg="green.50" p={4} borderRadius="lg">
                  <Text fontWeight="bold" mb={2}>50% Fixed</Text>
                  <Text fontSize="sm" color="gray.600">Rate certainty for budgeting</Text>
                </Box>
                <Box bg="blue.50" p={4} borderRadius="lg">
                  <Text fontWeight="bold" mb={2}>50% Variable</Text>
                  <Text fontSize="sm" color="gray.600">Flexibility and offset benefits</Text>
                </Box>
                <Box bg="purple.50" p={4} borderRadius="lg">
                  <Text fontWeight="bold" mb={2}>Balanced Risk</Text>
                  <Text fontSize="sm" color="gray.600">Protection from rate changes</Text>
                </Box>
              </SimpleGrid>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="1px" borderColor="gray.200" borderRadius="lg" mb={4}>
            <AccordionButton py={6} _expanded={{ bg: "blue.50" }}>
              <HStack flex={1} textAlign="left">
                <Icon as={FaCreditCard} boxSize={6} color="blue.600" />
                <Heading size="md">Offset Accounts & Redraw</Heading>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={6}>
              <Text color="gray.600" mb={4}>Maximise your savings while maintaining access to funds</Text>
              <Text fontSize="sm" color="gray.600">Offset accounts reduce interest without reducing deductibility</Text>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Container>

      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl">Tax Benefits</Heading>
            <Text fontSize="lg" color="gray.600">Maximise your investment property tax deductions</Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md" textAlign="center">
              <Icon as={FaFileInvoiceDollar} boxSize={10} color="teal.500" mb={4} />
              <Heading size="sm" mb={2}>Interest Deductions</Heading>
              <Text fontSize="sm" color="gray.600">100% tax deductible on investment loans</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md" textAlign="center">
              <Icon as={FaShieldAlt} boxSize={10} color="green.500" mb={4} />
              <Heading size="sm" mb={2}>Depreciation</Heading>
              <Text fontSize="sm" color="gray.600">Write off building & fixture value</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md" textAlign="center">
              <Icon as={FaPercentage} boxSize={10} color="blue.500" mb={4} />
              <Heading size="sm" mb={2}>Negative Gearing</Heading>
              <Text fontSize="sm" color="gray.600">Offset losses against taxable income</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md" textAlign="center">
              <Icon as={FaCheckCircle} boxSize={10} color="purple.500" mb={4} />
              <Heading size="sm" mb={2}>CGT Concessions</Heading>
              <Text fontSize="sm" color="gray.600">50% discount on capital gains</Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Box bg="teal.600" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaUniversity} boxSize={16} />
            <Heading size="xl">Optimise Your Finance Structure</Heading>
            <Text fontSize="lg">Speak with our finance strategists for personalised advice</Text>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyStrategyFinanceOptimization;
